import { AuthError } from "./authError";
import useAuth from "@/composables/auth";
import { ApiError } from "@/interfaces/api";
import type {
    TLoginSchema,
    TRegSchema,
    TAppleLoginSchema,
    TAppleRegSchema,
    TGoogleLoginSchema,
    TGoogleRegSchema,
    BankIDUserInfo,
} from "@/interfaces/api";
import { HTTP_STATUS_CODE } from "@/constants/request";
import { useUserStore } from "@/store/user";
import { LOGOUT_CLEAR_TIMEOUT } from "@/constants/system";

export { AUTH_MODE } from "@lib/constants/auth";

export type TGoogleApiClient = "web" | "ios" | "android";

export const PAGE_MODE = {
    MAIN: "main",
    OPTIONS: "options",
};

export type TApiExternalAuth = {
    redirectAuth?: boolean;
    already_exists?: boolean;
    wrongLoginMethod?: boolean;
};

export async function login(email: string, password: string, organization?: string) {
    const { setLoggedIn, api } = useNjordaApi();
    const payload: TLoginSchema = {
        email,
        password,
    };
    if (organization) {
        payload.organization = organization;
    }
    try {
        await api.postAppV1AuthLogin(payload);
        setLoggedIn(true);
        await updateUserInfo();
    } catch (error) {
        setLoggedIn(false);
        // Backend signals "this email was registered via a different auth
        // method" with error_code WRONG_AUTH_METHOD; translate to AuthError
        // so callers can show the WrongLoginMethodDialog.
        if (
            error instanceof ApiError &&
            error.body?.error_code === "WRONG_AUTH_METHOD"
        ) {
            throw new AuthError("Wrong login method", { wrongLoginMethod: true });
        }
        throw error;
    }
}

export async function register(
    name: string,
    email: string,
    password: string,
    organization?: string,
) {
    const { setLoggedIn, api } = useNjordaApi();
    const payload: TRegSchema = {
        email,
        password,
        name,
        accept_terms_conditions: true,
    };
    if (organization) {
        payload.organization = organization;
    }
    try {
        await api.postAppV1AuthRegister(payload);
        setLoggedIn(true);
        await updateUserInfo();
    } catch (error) {
        setLoggedIn(false);
        throw error;
    }
}

export async function confirmRegistrationApple(
    code: string,
    organization?: string | null,
) {
    const { setLoggedIn, api } = useNjordaApi();
    const payload: TAppleRegSchema = {
        code,
    };
    if (organization) {
        payload.organization = organization;
    }
    try {
        const { already_exists: alreadyExists } =
            (await api.postAppV1AuthRegisterApple(payload)) as TApiExternalAuth;

        if (alreadyExists) {
            throw new AuthError("User already exists", {
                authExists: true,
            });
        }
        setLoggedIn(true);
        await updateUserInfo();
    } catch (error) {
        setLoggedIn(false);
        throw error;
    }
}

export async function confirmLoginApple(code: string, organization?: string | null) {
    const { setLoggedIn, api } = useNjordaApi();
    const payload: TAppleLoginSchema = {
        code,
    };
    if (organization) {
        payload.organization = organization;
    }
    try {
        const res = (await api
            .postAppV1AuthLoginApple(payload)
            .then(() => ({}))
            .catch((e) => {
                if (e.status === HTTP_STATUS_CODE.NOT_FOUND) {
                    return {
                        redirectAuth: true,
                    };
                }
                if (e.status === HTTP_STATUS_CODE.BAD_REQUEST) {
                    return {
                        wrongLoginMethod: true,
                    };
                }
                return null;
            })) as TApiExternalAuth;
        if (!res) {
            throw new Error("Unexpected empty response from Apple login");
        }

        if (res.redirectAuth) {
            throw new AuthError("User not found", {
                authExists: true,
            });
        }
        if (res.wrongLoginMethod) {
            throw new AuthError("Wrong login method", {
                wrongLoginMethod: true,
            });
        }
        setLoggedIn(true);
        await updateUserInfo();
    } catch (error) {
        setLoggedIn(false);
        throw error;
    }
}

export async function confirmRegistrationGoogle(
    code: string,
    organization?: string | null,
    client = "web" as TGoogleApiClient,
) {
    const { setLoggedIn, api } = useNjordaApi();
    const payload: TGoogleRegSchema = {
        access_token: code,
    };
    if (organization) {
        payload.organization = organization;
    }
    try {
        const data = (await api.postAppV1AuthRegisterGoogle(client, payload).catch((e) => {
            if (e.status === HTTP_STATUS_CODE.CONFLICT) {
                return { already_exists: true };
            }
            return null;
        })) as TApiExternalAuth | null;

        if (!data) {
            throw new Error("Unexpected empty response from Google registration");
        }

        if (data.already_exists) {
            throw new AuthError("User already exists", {
                authExists: true,
            });
        }
        setLoggedIn(true);
        await updateUserInfo();
    } catch (error) {
        setLoggedIn(false);
        throw error;
    }
}

export async function confirmLoginGoogle(
    code: string,
    organization?: string | null,
    client = "web" as TGoogleApiClient,
) {
    const { setLoggedIn, api } = useNjordaApi();
    const payload: TGoogleLoginSchema = {
        access_token: code,
    };
    if (organization) {
        payload.organization = organization;
    }
    try {
        const res = (await api
            .postAppV1AuthLoginGoogle(client, payload)
            .then(() => ({} as TApiExternalAuth))
            .catch((e) => {
                if (e.status === HTTP_STATUS_CODE.NOT_FOUND) {
                    return {
                        redirectAuth: true,
                    };
                }
                if (e.status === HTTP_STATUS_CODE.BAD_REQUEST) {
                    return {
                        wrongLoginMethod: true,
                    };
                }
                return null;
            })) as TApiExternalAuth | null;
        if (!res) {
            throw new Error("Unexpected empty response from Google login");
        }
        if (res.redirectAuth) {
            throw new AuthError("User not found", {
                authExists: true,
            });
        }
        if (res.wrongLoginMethod) {
            throw new AuthError("Wrong login method", {
                wrongLoginMethod: true,
            });
        }
        setLoggedIn(true);
        await updateUserInfo();
    } catch (error) {
        setLoggedIn(false);
        throw error;
    }
}

export async function confirmBankIDAuth(
    userInfo: BankIDUserInfo & {
        alreadyExists?: boolean;
    },
) {
    const { setLoggedIn } = useNjordaApi();

    if (userInfo.alreadyExists) {
        useUserStore().setAlreadyExistsWhenRegistering(true);
    } else if (userInfo) {
        setLoggedIn(true);
        await updateUserInfo();
    }
}

export async function logout() {
    const { api } = useNjordaApi();
    const $router = useRouter();
    const { defaultRoute } = useAuth();
    try {
        await api.postAppV1AuthLogout();
    } catch (e) {
        console.error("Error logging out", e);
    }
    $router.push(defaultRoute);
    await clearUserLoginInfo();
}

export function clearUserLoginInfo() {
    const { setLoggedIn } = useNjordaApi();
    const userStore = useUserStore();
    setLoggedIn(false);
    return new Promise((resolve) => setTimeout(() => {
        userStore.clear();
        resolve(true);
    }, LOGOUT_CLEAR_TIMEOUT));
}

export async function updateUserInfo() {
    const userStore = useUserStore();
    const $router = useRouter();
    const { defaultRoute } = useAuth();
    await userStore.fetchMe().catch((err) => {
        if (err.status === 401) {
            clearUserLoginInfo();
            $router.push(defaultRoute);
            return;
        }
        throw err;
    });
}

export function setAlreadyExistsWhenRegistering() {
    const userStore = useUserStore();
    userStore.setAlreadyExistsWhenRegistering(true);
}
